/* ToolsMax theme.js — vanilla JS, no build step, no framework */
(() => {
  'use strict';

  /* ---------------- Scroll reveal ---------------- */
  const revealEls = document.querySelectorAll('[data-reveal]');
  if (revealEls.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = entry.target.getAttribute('data-reveal-delay') || 0;
          entry.target.style.transitionDelay = `${delay}ms`;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    revealEls.forEach((el, i) => {
      if (!el.hasAttribute('data-reveal-delay')) el.setAttribute('data-reveal-delay', String((i % 4) * 70));
      io.observe(el);
    });
  }

  /* ---------------- Header: scroll state + mobile menu ---------------- */
  const header = document.querySelector('[data-site-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }
  /* Both the hamburger button in the header AND the "X" close button inside
     the mobile menu drawer share the [data-menu-toggle] attribute, so every
     one of them must get its own click listener (querySelectorAll + forEach),
     not just the first one in DOM order (querySelector would silently skip
     the close button, since it comes later in the markup than the hamburger). */
  const menuToggles = document.querySelectorAll('[data-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  if (menuToggles.length && mobileMenu) {
    const hamburgerToggle = document.querySelector('.site-header__menu-toggle[data-menu-toggle]');
    menuToggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = mobileMenu.classList.toggle('is-open');
        if (hamburgerToggle) hamburgerToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.documentElement.classList.toggle('no-scroll', open);
      });
    });
  }

  /* ---------------- Desktop category nav: wheel-to-horizontal scroll ----------------
     When there are more categories than fit the header width, the bar scrolls
     horizontally via trackpad swipe or shift+wheel, but a plain vertical mouse-wheel
     scroll over it does nothing (it has no vertical overflow) — to most people with
     an ordinary mouse that reads as "it doesn't scroll". This converts an ordinary
     vertical wheel scroll into horizontal movement whenever the cursor is over the
     nav and it actually overflows. */
  const siteNav = document.querySelector('.site-header__nav');
  if (siteNav) {
    siteNav.addEventListener('wheel', (e) => {
      if (siteNav.scrollWidth <= siteNav.clientWidth) return;
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        siteNav.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    }, { passive: false });
  }

  /* ---------------- Accordions ---------------- */
  document.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion-item');
      const singleOpen = item.closest('[data-accordion-single]');
      if (singleOpen) {
        singleOpen.querySelectorAll('.accordion-item.is-open').forEach((openItem) => {
          if (openItem !== item) openItem.classList.remove('is-open');
        });
      }
      item.classList.toggle('is-open');
    });
  });

  /* ---------------- Cart drawer ---------------- */
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartOverlay = document.querySelector('[data-cart-overlay]');

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartOverlay && cartOverlay.classList.add('is-open');
    document.documentElement.classList.add('no-scroll');
  }
  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartOverlay && cartOverlay.classList.remove('is-open');
    document.documentElement.classList.remove('no-scroll');
  }
  /* Delegated (not bound once at page load with querySelectorAll+forEach) so
     these keep working even after refreshCartDrawer() below replaces
     [data-cart-drawer-inner]'s contents with fresh markup fetched from the
     server. The "X" close button lives inside that container, so every time
     a product is added, removed, or its quantity changes, a brand-new button
     element replaces the old one — a listener bound directly to the old node
     doesn't carry over, and the X silently stops closing the drawer. Delegating
     the click to `document` instead means it doesn't matter which physical
     node the click lands on, old or freshly re-rendered. */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) { openCartDrawer(); return; }
    if (e.target.closest('[data-cart-close]')) { closeCartDrawer(); }
  });
  cartOverlay && cartOverlay.addEventListener('click', closeCartDrawer);

  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    try {
      const res = await fetch('/?section_id=cart-drawer');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-drawer-inner]');
      const oldContent = cartDrawer.querySelector('[data-cart-drawer-inner]');
      if (newContent && oldContent) oldContent.innerHTML = newContent.innerHTML;
      const countEls = document.querySelectorAll('[data-cart-count]');
      const newCount = doc.querySelector('[data-cart-count]');
      if (newCount) countEls.forEach((el) => { el.textContent = newCount.textContent; });
    } catch (e) { /* no-op: network or markup mismatch, cart still works via normal page */ }
  }

  /* Intercept "add to cart" forms to add via AJAX + open drawer */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = form.getAttribute('data-adding-text') || '...'; }
    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('cart add failed');
      await refreshCartDrawer();
      openCartDrawer();
      showToast(form.getAttribute('data-success-text') || 'Lisätty ostoskoriin');
    } catch (err) {
      showToast(form.getAttribute('data-error-text') || 'Jokin meni pieleen', true);
    } finally {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
    }
  });

  /* Quick-add button on product cards (collection/search/carousel grids) */
  document.addEventListener('click', async (e) => {
    const quickBtn = e.target.closest('[data-quickadd]');
    if (!quickBtn) return;
    e.preventDefault();
    if (quickBtn.disabled) return;
    const variantId = quickBtn.getAttribute('data-variant-id');
    if (!variantId) return;
    const originalText = quickBtn.textContent;
    quickBtn.disabled = true;
    quickBtn.textContent = '...';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!res.ok) throw new Error('cart add failed');
      await refreshCartDrawer();
      openCartDrawer();
      showToast('Lisätty ostoskoriin');
    } catch (err) {
      showToast('Jokin meni pieleen', true);
    } finally {
      quickBtn.disabled = false;
      quickBtn.textContent = originalText;
    }
  });

  /* Quantity steppers (cart drawer + cart page) */
  document.addEventListener('click', async (e) => {
    const stepBtn = e.target.closest('[data-qty-step]');
    if (!stepBtn) return;
    const wrapper = stepBtn.closest('[data-qty-wrapper]');
    const input = wrapper.querySelector('input[type="number"]');
    const step = parseInt(stepBtn.getAttribute('data-qty-step'), 10);
    const newVal = Math.max(0, parseInt(input.value || '1', 10) + step);
    input.value = newVal;
    const key = wrapper.getAttribute('data-line-key');
    if (key) await updateCartLine(key, newVal);
  });

  async function updateCartLine(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error('cart update failed');
      await refreshCartDrawer();
      if (document.querySelector('[data-cart-page]')) window.location.reload();
    } catch (err) {
      showToast('Jokin meni pieleen', true);
    }
  }

  /* ---------------- Toast ---------------- */
  let toastEl;
  function showToast(message, isError) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.style.background = isError ? 'var(--color-red)' : 'var(--color-ink)';
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastEl._timer);
    toastEl._timer = setTimeout(() => toastEl.classList.remove('is-visible'), 2600);
  }

  /* ---------------- Simple carousel (featured products / testimonials) ---------------- */
  document.querySelectorAll('[data-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    const prev = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');
    if (!track) return;
    const scrollAmount = () => track.clientWidth * 0.85;
    prev && prev.addEventListener('click', () => track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => track.scrollBy({ left: scrollAmount(), behavior: 'smooth' }));
  });

  /* ---------------- Variant picker -> update price/image/availability ---------------- */
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const variantInputs = form.querySelectorAll('[data-variant-input]');
    if (!variantInputs.length) return;
    const variantsJson = form.querySelector('[data-variants-json]');
    if (!variantsJson) return;
    let variants = [];
    try { variants = JSON.parse(variantsJson.textContent); } catch (e) { variants = []; }

    function currentOptions() {
      return Array.from(variantInputs).reduce((acc, input) => {
        if (input.type === 'radio' && !input.checked) return acc;
        acc[Number(input.dataset.optionIndex)] = input.value;
        return acc;
      }, {});
    }
    function findVariant() {
      const opts = currentOptions();
      return variants.find((v) => v.options.every((val, i) => val === opts[i]));
    }
    function applyVariant() {
      const variant = findVariant();
      const idInput = form.querySelector('[name="id"]');
      const submitBtn = form.querySelector('[type="submit"]');
      const priceEl = document.querySelector('[data-product-price]');
      const compareEl = document.querySelector('[data-product-compare-price]');
      if (!variant) {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = form.getAttribute('data-unavailable-text') || 'Ei saatavilla'; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        submitBtn.textContent = variant.available
          ? (form.getAttribute('data-addtext') || 'Lisää ostoskoriin')
          : (form.getAttribute('data-unavailable-text') || 'Loppunut');
      }
      if (variant.featured_media && variant.featured_media.id) {
        const mediaEl = document.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (mediaEl) mediaEl.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
    }
    function formatMoney(cents) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
    variantInputs.forEach((input) => input.addEventListener('change', applyVariant));
    applyVariant();
  });

  /* ---------------- Product gallery thumbnails ---------------- */
  document.querySelectorAll('[data-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const main = document.getElementById('MainProductImage');
      if (main) main.src = thumb.getAttribute('data-full-src');
      thumb.parentElement.querySelectorAll('[data-thumb]').forEach((b) => b.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });

  /* ---------------- Related products (recommendations API) ---------------- */
  const relatedEl = document.querySelector('[data-related-products]');
  if (relatedEl) {
    const url = relatedEl.getAttribute('data-url');
    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newEl = doc.querySelector('[data-related-products]');
        if (newEl && newEl.innerHTML.trim()) relatedEl.innerHTML = newEl.innerHTML;
        relatedEl.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
      })
      .catch(() => {});
  }

  /* ---------------- Predictive search (minimal) ---------------- */
  const searchInput = document.querySelector('[data-search-input]');
  const searchResults = document.querySelector('[data-search-results]');
  if (searchInput && searchResults) {
    let debounceTimer;
    searchInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const q = searchInput.value.trim();
      if (q.length < 2) { searchResults.innerHTML = ''; searchResults.hidden = true; return; }
      debounceTimer = setTimeout(async () => {
        try {
          const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`);
          const data = await res.json();
          const products = data.resources.results.products || [];
          searchResults.innerHTML = products.length
            ? products.map((p) => `
                <a class="search-result" href="${p.url}">
                  <img src="${p.featured_image ? p.featured_image.url : ''}" width="48" height="48" alt="" loading="lazy">
                  <span>${p.title}</span>
                </a>`).join('')
            : `<p class="search-result search-result--empty">Ei tuloksia</p>`;
          searchResults.hidden = false;
        } catch (e) { /* ignore */ }
      }, 220);
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-search-wrapper]')) searchResults.hidden = true;
    });
  }
})();
